// @ts-nocheck
/**
 * Custom Treebeard Component - Modern React replacement for react-treebeard
 *
 * This component provides a drop-in replacement for react-treebeard with:
 * - No defaultProps warnings (uses JavaScript default parameters)
 * - Same API surface for backwards compatibility
 * - TypeScript support
 */

import React, { useCallback } from 'react';

// Types
export interface TreeNode {
  id?: string;
  name: string;
  toggled?: boolean;
  active?: boolean;
  children?: TreeNode[];
  loading?: boolean;
  elementType?: string;
  contentType?: string;
  [key: string]: any;
}

export interface TreeStyle {
  tree?: {
    base?: React.CSSProperties;
    node?: {
      base?: React.CSSProperties;
      customContainer?: {
        node?: React.CSSProperties;
        link?: React.CSSProperties;
        activeLink?: React.CSSProperties;
      };
      toggle?: {
        base?: React.CSSProperties;
      };
      header?: {
        base?: React.CSSProperties;
        connector?: React.CSSProperties;
        title?: React.CSSProperties;
        titleIcon?: React.CSSProperties;
        titleText?: React.CSSProperties;
        enableTitleText?: React.CSSProperties;
        activeTitleText?: React.CSSProperties;
      };
      subtree?: React.CSSProperties;
      loading?: React.CSSProperties;
    };
  };
}

export interface TreeDecorators {
  Loading?: React.ComponentType<any>;
  Toggle?: React.ComponentType<{ style: any; node?: TreeNode }>;
  Header?: React.ComponentType<{ style: any; node: TreeNode }>;
  Container?: React.ComponentType<{
    style: any;
    decorators: TreeDecorators;
    terminal: boolean;
    onClick: () => void;
    node: TreeNode;
    animations?: boolean;
  }>;
}

export interface TreebeardProps {
  data: TreeNode | TreeNode[];
  onToggle?: (node: TreeNode, toggled: boolean) => void;
  style?: TreeStyle;
  decorators?: TreeDecorators;
  animations?: boolean | object;
}

// Default decorators
const DefaultToggle: React.FC<{ style: any }> = ({ style }) => (
  <div className="node-arrow-icon1" style={style?.base}>
    <i className="fa fa-angle-right"></i>
  </div>
);

const DefaultHeader: React.FC<{ style: any; node: TreeNode }> = ({ style, node }) => (
  <div style={style?.base}>
    <div style={style?.title}>
      <span style={style?.titleText}>{node.name}</span>
    </div>
  </div>
);

const DefaultLoading: React.FC<{ style: any }> = ({ style }) => (
  <div style={style}>Loading...</div>
);

interface ContainerProps {
  style: any;
  decorators: TreeDecorators;
  terminal: boolean;
  onClick: () => void;
  node: TreeNode;
  animations?: boolean;
}

const DefaultContainer: React.FC<ContainerProps> = ({
  style,
  decorators,
  terminal,
  onClick,
  node
}) => {
  let containerStyle = style?.customContainer?.link || {};

  if (node.active) {
    containerStyle = {
      ...containerStyle,
      ...(style?.customContainer?.activeLink || {})
    };
  }

  if (!terminal) {
    containerStyle = {
      ...containerStyle,
      ...(style?.customContainer?.node || {})
    };
  }

  const HeaderComponent = decorators.Header || DefaultHeader;
  const ToggleComponent = decorators.Toggle || DefaultToggle;

  return (
    <div
      className={`tree-node ${node.active ? 'active' : ''} ${node.toggled ? 'toggled' : ''}`}
      onClick={onClick}
      style={containerStyle}
    >
      {!terminal && <ToggleComponent style={style?.toggle} node={node} />}
      <HeaderComponent node={node} style={style?.header} />
    </div>
  );
};

// TreeNode component
interface TreeNodeComponentProps {
  node: TreeNode;
  onToggle?: (node: TreeNode, toggled: boolean) => void;
  style?: TreeStyle['tree']['node'];
  decorators: TreeDecorators;
  animations?: boolean;
  level?: number;
}

const TreeNodeComponent: React.FC<TreeNodeComponentProps> = ({
  node,
  onToggle,
  style = {},
  decorators,
  animations = false,
  level = 0
}) => {
  // A node is terminal (leaf) if it has no children AND it's not a lazy-loadable node
  // For lazy loading: PROJECT nodes should always show toggle (they always have children to load)
  // Also GROUP nodes can be lazy loaded when empty
  const isProject = node.elementType === 'PROJECT';
  const isLazyLoadableGroup = node.elementType === 'GROUP' && (!node.children || node.children.length === 0);
  const terminal = (!node.children || node.children.length === 0) && !isProject && !isLazyLoadableGroup;

  const handleClick = useCallback(() => {
    if (onToggle) {
      onToggle(node, !node.toggled);
    }
  }, [node, onToggle]);

  const ContainerComponent = decorators.Container || DefaultContainer;
  const LoadingComponent = decorators.Loading || DefaultLoading;

  return (
    <li style={style?.base} data-id={node.id}>
      <ContainerComponent
        style={style}
        decorators={decorators}
        terminal={terminal}
        onClick={handleClick}
        node={node}
        animations={animations}
      />
      {node.loading && <LoadingComponent style={style?.loading} />}
      {!terminal && node.toggled && node.children && (
        <ul style={style?.subtree}>
          {node.children.map((child, index) => (
            <TreeNodeComponent
              key={child.id || `${node.name}-${index}`}
              node={child}
              onToggle={onToggle}
              style={style}
              decorators={decorators}
              animations={animations}
              level={level + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

TreeNodeComponent.displayName = 'TreeNodeComponent';

// Main Treebeard component
export const Treebeard: React.FC<TreebeardProps> = ({
  data,
  onToggle,
  style = {},
  decorators = {},
  animations = false
}) => {
  const treeStyle = style?.tree || {};
  const nodeStyle = treeStyle?.node || {};

  const mergedDecorators: TreeDecorators = {
    Loading: decorators.Loading || DefaultLoading,
    Toggle: decorators.Toggle || DefaultToggle,
    Header: decorators.Header || DefaultHeader,
    Container: decorators.Container || DefaultContainer,
    ...decorators
  };

  // Handle both single node and array of nodes
  const nodes = Array.isArray(data) ? data : [data];

  return (
    <ul style={treeStyle?.base}>
      {nodes.map((node, index) => (
        <TreeNodeComponent
          key={node.id || `root-${index}`}
          node={node}
          onToggle={onToggle}
          style={nodeStyle}
          decorators={mergedDecorators}
          animations={animations}
        />
      ))}
    </ul>
  );
};

// Also export decorators base object for customization (matching react-treebeard API)
export const decorators: TreeDecorators = {
  Loading: DefaultLoading,
  Toggle: DefaultToggle,
  Header: DefaultHeader,
  Container: DefaultContainer
};

export default Treebeard;
